import { createSlice, nanoid } from '@reduxjs/toolkit';

const programSlice = createSlice({
  name: 'program',
  initialState: {
    data: {}, // Array of programs
  },
  reducers: {
    // Program Management
    createProgram(state, action) {
      const newProgram = {
        key: action.payload.key || nanoid(),
        id: action.payload.id || nanoid(),
        name: action.payload.name,
        description: action.payload.description || null,
        coach: action.payload.coach,
        type: action.payload.type,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        workouts: action.payload.workouts|| [], // Will contain workout objects with exercises
      };
      state.data = newProgram;
    },

    deleteProgram(state, action) {
      state.data = state.data.filter(program => program.key !== action.payload);
      if (state.activeProgram === action.payload) {
        state.activeProgram = null;
      }
    },

    setActiveProgram(state, action) {
      state.activeProgram = action.payload;
    },

    updateProgramInfo(state, action) {
      const { programName } = action.payload;
      const program = state.data;
      
      if (program) {
        program.name = programName;
        program.updatedAt = new Date().toISOString();
      }
      
      // ✅ Usar orderInType en lugar del index global
      program.workouts.forEach((workout) => {
        workout.name = `${programName} ${workout.type}${workout.orderInType}`;
      });
    },
    updateProgramType(state, action) {
      const { programType } = action.payload;
      const program = state.data;
      
      if (program) {
        program.type = programType;
        program.updatedAt = new Date().toISOString();
      }
    },

    // Workout Management within a Program
    addNewWorkoutToProgram(state, action) {
      const { id, key, workoutName, type, orderInType } = action.payload;
      const program = {...state.data};
      
      if (program) {
        const newWorkout = {
          id: id,
          key: key,
          name: workoutName || `Workout ${program.workouts.length + 1}`,
          order: program.workouts.length + 1,
          type: type,
          orderInType: orderInType,
          exercises: [],
        };
        program.workouts.push(newWorkout);
        program.updatedAt = new Date().toISOString();
      }
      state.data = program;
      programSlice.caseReducers.reorderWorkouts(state);
    },

    // Añade este reducer dentro de programSlice.reducers

    reorderWorkouts(state) {
      const program = state.data;
      
      if (program && program.workouts && program.workouts.length > 0) {
        // Obtener todos los tipos únicos y ordenarlos alfabéticamente
        const uniqueTypes = [...new Set(program.workouts.map(w => w.type))].sort();
        const numberOfTypes = uniqueTypes.length;
        
        // Crear un mapa de tipo a su posición
        const typePositions = {};
        uniqueTypes.forEach((type, index) => {
          typePositions[type] = index + 1;
        });
        
        // Ordenar workouts según la fórmula
        program.workouts.sort((a, b) => {
          const orderA = (a.orderInType - 1) * numberOfTypes + typePositions[a.type];
          const orderB = (b.orderInType - 1) * numberOfTypes + typePositions[b.type];
          return orderA - orderB;
        });
        
        // Asignar order secuencial después de ordenar
        program.workouts.forEach((workout, index) => {
          workout.order = index + 1;
        });
        
        program.updatedAt = new Date().toISOString();
      }
    },

    removeNewWorkoutFromProgram(state, action) {
      const { workoutKey } = action.payload;
      
      // Opción 2: Encontrar el índice y usar splice (mutación directa con Immer)
      const index = state.data.workouts.findIndex(w => w.key === workoutKey);
      if (index !== -1) {
        state.data.workouts.splice(index, 1);
        
        // Reorder remaining workouts
        programSlice.caseReducers.reorderWorkouts(state);
        
        state.data.updatedAt = new Date().toISOString();
      } else {
        console.warn("Workout delete from Program: Workout not found with key:", workoutKey);
      }
    },

    changeWorkoutOrder(state, action) {
      const { programKey, workoutKey, direction } = action.payload;
      const program = state.data.find(p => p.key === programKey);
      
      if (program) {
        const workoutIndex = program.workouts.findIndex(w => w.key === workoutKey);
        
        if (workoutIndex === -1) return;
        
        if (direction === 'up' && workoutIndex > 0) {
          // Swap with previous
          [program.workouts[workoutIndex], program.workouts[workoutIndex - 1]] = 
          [program.workouts[workoutIndex - 1], program.workouts[workoutIndex]];
        } else if (direction === 'down' && workoutIndex < program.workouts.length - 1) {
          // Swap with next
          [program.workouts[workoutIndex], program.workouts[workoutIndex + 1]] = 
          [program.workouts[workoutIndex + 1], program.workouts[workoutIndex]];
        }
        
        // Update order numbers
        program.workouts = program.workouts.map((workout, index) => ({
          ...workout,
          order: index + 1
        }));
        program.updatedAt = new Date().toISOString();
      }
    },

    // Exercise Management within a Workout
    addNewExerciseToWorkouts(state, action) {
      const { key, type, exerciseId, exerciseName } = action.payload;
      state.data.workouts.forEach((workout, index) => {
        if(workout.type === type){
          const newExercise = {
            key: key,
            id: exerciseId,
            name: exerciseName,
            order: workout.exercises.length + 1,
            note: null,
            superset: null,
            sets: [
              {
                id: nanoid(),
                index: 1,
                kg: "",
                reps: 10,
                rpe: "",
                rir: "",
                tut: "",
                time: "",
                rest: '1\'30"',
                percentage_rm: "",
                note: "",
                superset_id:""
              },
              {
                id: nanoid(),
                index: 2,
                kg: "",
                reps: 10,
                rpe: "",
                rir: "",
                tut: "",
                time: "",
                rest: '1\'30"',
                percentage_rm: "",
                note: "",
                superset_id:""
              },
              {
                id: nanoid(),
                index: 3,
                kg: "",
                reps: 10,
                rpe: "",
                rir: "",
                tut: "",
                time: "",
                rest: '1\'30"',
                percentage_rm: "",
                note: "",
                superset_id:""
              }
            ]
          };
          workout.exercises.push(newExercise);
        }
      });
      state.data.updatedAt = new Date().toISOString();
    },

    //utilizado cuando ya existe el ejercicio y se añade un nuevo workout al tipo
    addNewExerciseToWorkout(state, action) {
      const { key, workoutKey, exerciseId, exerciseName } = action.payload;
      const workout = state.data.workouts.find(w => w.key === workoutKey);
        if(workout){
          let existingNote = null;
          let existingSuperSet = null;
          // Recorrer todos los workouts buscando el ejercicio con la misma key
          for (const w of state.data.workouts) {
            const existingExercise = w.exercises.find(ex => ex.key === key);
            if (existingExercise && existingExercise.note !== null) {
              existingNote = existingExercise.note;
              break; // Detener la búsqueda al encontrar la primera nota
            }
            if (existingExercise && existingExercise.superset !== null) {
              existingSuperSet = existingExercise.superset;
              break; // Detener la búsqueda al encontrar el primer superset
            }
          }
          const newExercise = {
            key: key,
            id: exerciseId,
            name: exerciseName,
            order: workout.exercises.length + 1,
            note: existingNote,
            superset: existingSuperSet,
            sets: [
              {
                id: nanoid(),
                index: 1,
                kg: "",
                reps: 10,
                rpe: "",
                rir: "",
                tut: "",
                time: "",
                rest: '1\'30"',
                percentage_rm: "",
                note: "",
                superset_id:""
              },
              {
                id: nanoid(),
                index: 2,
                kg: "",
                reps: 10,
                rpe: "",
                rir: "",
                tut: "",
                time: "",
                rest: '1\'30"',
                percentage_rm: "",
                note: "",
                superset_id:""
              },
              {
                id: nanoid(),
                index: 3,
                kg: "",
                reps: 10,
                rpe: "",
                rir: "",
                tut: "",
                time: "",
                rest: '1\'30"',
                percentage_rm: "",
                note: "",
                superset_id:""
              }
            ]
          };
          workout.exercises.push(newExercise);
          state.data.updatedAt = new Date().toISOString();
        }else{
          console.warn("Method addNewExerciseToWorkout. Workout not found with key:", workoutKey);
        }
    },

    removeNewExerciseFromWorkouts(state, action) {
      const { exerciseKey } = action.payload;
      state.data.workouts.forEach((workout, index) => {
        workout.exercises = workout.exercises.filter(e => e.key !== exerciseKey);
        // Reorder exercises
        workout.exercises = workout.exercises.map((exercise, index) => ({
          ...exercise,
          order: index + 1
        }));
      })
      state.data.updatedAt = new Date().toISOString();
      
    },

    removeNewExerciseFromWorkout(state, action) {
      const { programKey, workoutKey, exerciseKey } = action.payload;
      const program = state.data.find(p => p.key === programKey);
      
      if (program) {
        const workout = program.workouts.find(w => w.key === workoutKey);
        
        if (workout) {
          workout.exercises = workout.exercises.filter(e => e.key !== exerciseKey);
          // Reorder exercises
          workout.exercises = workout.exercises.map((exercise, index) => ({
            ...exercise,
            order: index + 1
          }));
          program.updatedAt = new Date().toISOString();
        }
      }
    },

    changeExerciseOrder(state, action) {
      const { exerciseKey, direction } = action.payload;
        const program = state.data;
        program.workouts.forEach((workout, index) => {
          if (workout) {
            const targetExercise = workout.exercises.find(e => e.key === exerciseKey);
            if (!targetExercise) return;

            let movingExercises = [];
            
            // Handle superset groups
            if (targetExercise.superset !== null) {
              movingExercises = workout.exercises.filter(e => e.superset === targetExercise.superset);
            } else {
              movingExercises = [targetExercise];
            }
            if (direction === 'up') {
              const previousExercise = workout.exercises.find(e => e.order === movingExercises[0].order - 1);
              if (!previousExercise) return;

              let previousExercises = [];
              if (previousExercise.superset !== null) {
                previousExercises = workout.exercises.filter(e => e.superset === previousExercise.superset);
              } else {
                previousExercises = [previousExercise];
              }

              movingExercises.forEach(e => e.order -= previousExercises.length);
              previousExercises.forEach(e => e.order += movingExercises.length);
            } else if (direction === 'down') {
              const nextExercise = workout.exercises.find(e => e.order === movingExercises[movingExercises.length - 1].order + 1);
              if (!nextExercise) return;

              let nextExercises = [];
              if (nextExercise.superset !== null) {
                nextExercises = workout.exercises.filter(e => e.superset === nextExercise.superset);
              } else {
                nextExercises = [nextExercise];
              }

              movingExercises.forEach(e => e.order += nextExercises.length);
              nextExercises.forEach(e => e.order -= movingExercises.length);
            }
            workout.exercises.sort((a, b) => a.order - b.order);
          }
        })
        program.updatedAt = new Date().toISOString();
    },

    // Set Management
    addSetToExerciseInType(state, action) {
      const { exerciseKey } = action.payload;
      const program = state.data;
      
      program.workouts.forEach((workout)=>{
        const exercise = workout.exercises.find(e => e.key === exerciseKey);
        if (exercise) {
          const newSet = {
            id: nanoid(),
            index: exercise.sets.length + 1,
            kg: "",
            reps: 10,
            rpe: "",
            rir: "",
            tut: "",
            time: "",
            rest: '1\'30"',
            percentage_rm: "",
            note: "",
            superset_id:""
          };
          exercise.sets.push(newSet);
          program.updatedAt = new Date().toISOString();
        }
      });
    },

    addSetToExercise(state, action) {
      const { workoutId, exerciseKey } = action.payload;
      const program = state.data;
      const workout = program.workouts.find(w =>w.id === workoutId);
      const exercise = workout.exercises.find(e => e.key === exerciseKey);
      if (exercise) {
        const newSet = {
          id: nanoid(),
          index: exercise.sets.length + 1,
          kg: "",
          reps: 10,
          rpe: "",
          rir: "",
          tut: "",
          time: "",
          rest: '1\'30"',
          percentage_rm: "",
          note: "",
          superset_id:""
        };
        exercise.sets.push(newSet);
        program.updatedAt = new Date().toISOString();
      }
    },

    removeSetFromExercise(state, action) {
      const { workoutId, exerciseKey, setId } = action.payload;
      const program = state.data;
      
      if (program) {
        const workout = program.workouts.find(w => w.id === workoutId);
        if (workout) {
          const exercise = workout.exercises.find(e => e.key === exerciseKey);
          if (exercise) {
            exercise.sets = exercise.sets.filter(s => s.id !== setId);
            // Reindex sets
            exercise.sets = exercise.sets.map((set, index) => ({
              ...set,
              index: index + 1
            }));
            program.updatedAt = new Date().toISOString();
          }
        }
      }
    },

    updateSet(state, action) {
      const { programKey, workoutKey, exerciseKey, setId, updates } = action.payload;
      const program = state.data.find(p => p.key === programKey);
      
      if (program) {
        const workout = program.workouts.find(w => w.key === workoutKey);
        if (workout) {
          const exercise = workout.exercises.find(e => e.key === exerciseKey);
          if (exercise) {
            const set = exercise.sets.find(s => s.id === setId);
            if (set) {
              Object.assign(set, updates);
              program.updatedAt = new Date().toISOString();
            }
          }
        }
      }
    },

    // Superset Management
    createSuperset(state, action) {
      const { programKey, workoutKey, exerciseKeys } = action.payload;
      const program = state.data.find(p => p.key === programKey);
      
      if (program) {
        const workout = program.workouts.find(w => w.key === workoutKey);
        if (workout) {
          const newGroup = nanoid();
          workout.exercises = workout.exercises.map(exercise => {
            if (exerciseKeys.includes(exercise.key)) {
              return { ...exercise, superset: newGroup };
            }
            return exercise;
          });
          program.updatedAt = new Date().toISOString();
        }
      }
    },

    editSetValue(state, action){
      const { setId, field, exerciseKey, workoutId, value  } = action.payload;
      const workout = state.data.workouts.find( w => w.id === workoutId);
      const exercise = workout.exercises.find(ex => ex.key === exerciseKey);
      const set = exercise.sets.find(s=> s.id === setId);
      set[field] = value;
      state.data.updatedAt = new Date().toISOString();
    },

    editNewExerciseNote(state, action) {
      const { exerciseKey, note } = action.payload;
      
      // Validar que tenemos los datos necesarios
      if (!exerciseKey || note === undefined) {
        console.warn('editNewExerciseNote: exerciseKey o note faltantes');
        return;
      }
      
      // Buscar y actualizar el ejercicio
      let exerciseFound = false;
      
      for (const workout of state.data.workouts) {
        const exercise = workout.exercises.find(e => e.key === exerciseKey);
        
        if (exercise) {
          exercise.note = note;
          exerciseFound = true;
        }
      }
      
      // Opcional: log si no se encuentra
      if (!exerciseFound) {
        console.warn(`Ejercicio con key ${exerciseKey} no encontrado`);
        return;
      }
      
      state.data.updatedAt = new Date().toISOString();
    },

    switchNewExercise(state, action){
      const {type, currentExerciseKey, newExercise} = action.payload;
      state.data.workouts.forEach((workout, index) => {
        if(workout.type === type){
          const exercise = workout.exercises.find(ex => ex.key ===currentExerciseKey);
          exercise.id = newExercise.id;
          exercise.name = newExercise.name;
        }
      })
      state.data.updatedAt = new Date().toISOString();
    },

    addNewToSuperset(state, action) {
      const { exerciseKey, type, supersetId } = action.payload; // ✅ Recibir supersetId opcional
      
      // Recorrer todos los workouts del tipo especificado
      state.data.workouts.forEach((workout) => {
        if (workout.type === type) {
          // Encontrar el índice del ejercicio clickeado
          const exerciseIndex = workout.exercises.findIndex(ex => ex.key === exerciseKey);
          
          // Verificar que existe y que hay un siguiente ejercicio
          if (exerciseIndex !== -1 && exerciseIndex < workout.exercises.length - 1) {
            const currentExercise = workout.exercises[exerciseIndex];
            const nextExercise = workout.exercises[exerciseIndex + 1];
            
            // Determinar qué ID de superset usar
            let finalSupersetId;
            
            if (currentExercise.superset) {
              finalSupersetId = currentExercise.superset;
            } else if (nextExercise.superset) {
              finalSupersetId = nextExercise.superset;
            } else {
              // ✅ Usar el ID pasado desde el componente, o generar uno nuevo si no viene
              finalSupersetId = supersetId || nanoid();
            }
            
            // Asignar el superset a ambos ejercicios
            currentExercise.superset = finalSupersetId;
            nextExercise.superset = finalSupersetId;
          }
        }
      });
      
      state.data.updatedAt = new Date().toISOString();
    },

    removeNewFromSuperset(state, action) {
      const { exerciseKey, type } = action.payload; // Necesitamos el type para saber en qué workouts trabajar
      
      state.data.workouts.forEach((workout) => {
        if (workout.type === type) {
          const exercises = workout.exercises;
          
          // Encontrar el ejercicio objetivo
          const targetIndex = exercises.findIndex(ex => ex.key === exerciseKey);
          if (targetIndex === -1) return;
          
          const targetExercise = exercises[targetIndex];
          if (!targetExercise.superset) return;
          
          const supersetId = targetExercise.superset;
          
          // Encontrar todos los ejercicios de la superserie (consecutivos)
          let startIndex = targetIndex;
          let endIndex = targetIndex;
          
          // Buscar hacia atrás
          while (startIndex > 0 && exercises[startIndex - 1].superset === supersetId) {
            startIndex--;
          }
          
          // Buscar hacia adelante
          while (endIndex < exercises.length - 1 && exercises[endIndex + 1].superset === supersetId) {
            endIndex++;
          }
          
          const supersetLength = endIndex - startIndex + 1;
          
          // CASO 1: Superserie de 2 ejercicios - quitar superset de ambos, mantener orden
          if (supersetLength === 2) {
            exercises[startIndex].superset = null;
            exercises[endIndex].superset = null;
          } 
          // CASO 2: Superserie de más de 2 ejercicios
          else {
            // Es el primer ejercicio de la superserie
            if (targetIndex === startIndex) {
              // Quitar superset del ejercicio clickeado
              targetExercise.superset = null;
              
              // Extraer el ejercicio
              const [removed] = exercises.splice(targetIndex, 1);
              
              // El resto de la superserie (ahora en startIndex...endIndex-1) se mueve después del removido
              // Como removimos un elemento, los índices cambiaron
              const remainingSupersetLength = supersetLength - 1;
              const supersetBlock = exercises.splice(startIndex, remainingSupersetLength);
              
              // Insertar: primero el ejercicio removido, luego el bloque de superserie
              exercises.splice(startIndex, 0, removed, ...supersetBlock);
            } 
            // No es el primer ejercicio de la superserie
            else {
              // Quitar superset del ejercicio clickeado
              targetExercise.superset = null;
              
              // Extraer el ejercicio
              const [removed] = exercises.splice(targetIndex, 1);
              
              // Como removimos un elemento, endIndex cambió
              // Insertar el ejercicio después del último elemento de la superserie
              exercises.splice(endIndex, 0, removed);
            }
          }
          
          // Actualizar el order de todos los ejercicios
          exercises.forEach((ex, idx) => {
            ex.order = idx + 1;
          });
        }
      });
      
      state.data.updatedAt = new Date().toISOString();
    },

    // Clear all programs
    clearPrograms(state) {
      state.data = [];
      state.activeProgram = null;
    }
  },
});

export const {
  createProgram,
  deleteProgram,
  setActiveProgram,
  updateProgramInfo,
  updateProgramType,
  addNewWorkoutToProgram,
  removeNewWorkoutFromProgram,
  changeWorkoutOrder,
  addNewExerciseToWorkouts,
  addNewExerciseToWorkout,
  removeNewExerciseFromWorkouts,
  removeNewExerciseFromWorkout,
  changeExerciseOrder,
  addSetToExerciseInType,
  addSetToExercise,
  removeSetFromExercise,
  updateSet,
  createSuperset,
  reorderWorkouts,
  editSetValue,
  editNewExerciseNote,
  switchNewExercise,
  addNewToSuperset,
  removeNewFromSuperset,
  clearPrograms
} = programSlice.actions;

export const programReducer = programSlice.reducer;